import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Lottie from './Lottie'
import { savedItemsService } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { buildEditItemPayload, formatEuroFr } from '../utils/editProductOutcome'
import { describeLibraryListState, filterSavedItems, isolateLibraryEdit, savedItemsQueryKey } from '../utils/libraryIsolation'
import { getUserId } from '../utils/sessionUser'
import { resolveCardImage } from '../utils/itemMeta'
import { useProductCardImage } from '../hooks/useProductCardImage'
import { inferCardsColumn, placeLibraryPanel } from '../utils/placeLibraryPanel'
import CloseGlyph from './CloseGlyph'
import '../styles/scrollbar.css'
import './LibraryPanel.css'

function MissingImage({ compact = false }) {
  return (
    <span className={`library-missing${compact ? ' is-compact' : ''}`}>
      <img src="/Icon photo size select actual.png" alt="" />
      <span>Image indisponible</span>
    </span>
  )
}

function LibraryThumb({ item, compact = false, alt = '', onClick }) {
  const { src, onError } = useProductCardImage(item)
  if (!src) return <MissingImage compact={compact} />
  return <img src={src} alt={alt} onError={onError} onClick={onClick} />
}

export default function LibraryPanel({
  isOpen,
  onClose,
  items = [],
  isLoading = false,
  isError = false,
  errorMessage = '',
  importSuccessItem = null,
  onAddToCart,
  onDelete,
  onImageError,
  adding = false,
  deleting = false,
  embedded = false,
  anchorRef = null,
  onPlacementChange,
}) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const userId = getUserId(user)
  const savedKey = savedItemsQueryKey(userId)
  const reducedMotion = usePrefersReducedMotion()
  const [query, setQuery] = useState('')
  const [view, setView] = useState('list')
  const [selected, setSelected] = useState(null)
  const [draft, setDraft] = useState(null)
  const [editError, setEditError] = useState('')
  const [panelBox, setPanelBox] = useState(null)

  const visible = useMemo(() => filterSavedItems(items, query), [items, query])

  useEffect(() => {
    if (!isOpen) {
      setView('list')
      setSelected(null)
      setDraft(null)
      setEditError('')
      setPanelBox(null)
    }
  }, [isOpen])

  useLayoutEffect(() => {
    if (embedded) return undefined
    if (!isOpen) {
      onPlacementChange?.({ mode: 'closed', workspacePadRight: 0 })
      return undefined
    }

    const update = () => {
      const viewport = window.visualViewport
      const vw = Math.round(viewport?.width ?? window.innerWidth)
      const vh = Math.round(viewport?.height ?? window.innerHeight)
      const next = placeLibraryPanel({
        vw,
        vh,
        cards: inferCardsColumn({ vw }),
      })
      setPanelBox(next)
      onPlacementChange?.(next)
    }

    update()
    const node = anchorRef?.current
    const observer = typeof ResizeObserver === 'function' && node ? new ResizeObserver(update) : null
    observer?.observe(node)
    window.addEventListener('resize', update)
    window.visualViewport?.addEventListener('resize', update)
    window.visualViewport?.addEventListener('scroll', update)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', update)
      window.visualViewport?.removeEventListener('resize', update)
      window.visualViewport?.removeEventListener('scroll', update)
    }
  }, [isOpen, embedded, anchorRef, onPlacementChange])

  const updateMutation = useMutation({
    mutationFn: ({ savedId, payload }) => savedItemsService.update(savedId, payload),
    onMutate: async ({ savedId, payload }) => {
      await queryClient.cancelQueries({ queryKey: savedKey })
      const previousSaved = queryClient.getQueryData(savedKey)
      const isolated = isolateLibraryEdit({
        savedItems: previousSaved,
        cartItems: [],
        savedId,
        payload,
      })
      queryClient.setQueryData(savedKey, isolated.savedItems)
      return { previousSaved }
    },
    onError: (error, _vars, context) => {
      if (context?.previousSaved) {
        queryClient.setQueryData(savedKey, context.previousSaved)
      }
      setEditError(error?.response?.data?.detail || error.message || 'Impossible d’enregistrer.')
    },
    onSuccess: (updated, { savedId }) => {
      queryClient.setQueryData(savedKey, (current) => (
        isolateLibraryEdit({
          savedItems: current,
          cartItems: [],
          savedId,
          payload: updated,
        }).savedItems
      ))
      setSelected(updated)
      setView('list')
      setEditError('')
    },
  })

  if (!isOpen) return null

  const openPreview = (item) => {
    const resolved = resolveCardImage(item)
    if (!resolved.url && !resolved.key) return
    setSelected(item)
    setView('preview')
  }

  const openEdit = (item) => {
    setSelected(item)
    setDraft({
      name: item.name || '',
      link: item.link || '',
      price: item.price ?? 0,
      quantity: item.quantity || 1,
      promo: item.promo || '',
      image_url: item.image_url || '',
      image_key: item.image_key || '',
    })
    setEditError('')
    setView('edit')
  }

  const saveEdit = (event) => {
    event.preventDefault()
    if (!selected?.id || updateMutation.isPending) return
    const payload = buildEditItemPayload(draft, { item: selected })
    if (!payload.name) {
      setEditError('Le nom est obligatoire.')
      return
    }
    updateMutation.mutate({ savedId: selected.id, payload })
  }

  return (
    <div className={`library-panel-root${embedded ? ' is-embedded' : ''}`} data-testid="library-panel">
      <aside
        className={`lg-panel library-panel${reducedMotion ? '' : ' is-animated'}${panelBox ? ` is-placed is-mode-${panelBox.mode}` : ''}`}
        role="complementary"
        aria-labelledby="library-panel-title"
        style={!embedded && panelBox ? {
          left: panelBox.left,
          width: panelBox.width,
          bottom: panelBox.bottom,
          maxHeight: panelBox.maxHeight,
          right: 'auto',
        } : undefined}
      >
        <div className="lg-panel-frost" />
        <div className="lg-panel-tint" />
        <div className="lg-panel-shine" />
        <div className="lg-panel-body library-panel-body">
          <header className="library-panel-header">
            <div className="library-panel-heading">
              <img src="/Icon book.png" alt="" />
              <h2 id="library-panel-title">Bibliothèque</h2>
            </div>
            <button
              type="button"
              className="library-panel-close"
              onClick={onClose}
              aria-label="Fermer la bibliothèque"
            >
              <CloseGlyph />
            </button>
          </header>

          {view !== 'list' ? (
            <button type="button" className="library-panel-back" onClick={() => setView('list')}>
              <img src="/Icon arrow back ios new.png" alt="" />
              Retour à la liste
            </button>
          ) : null}

          {view === 'list' ? (
            <label className="library-panel-search-wrap">
              <img src="/Icon search.png" alt="" />
              <input
                type="search"
                inputMode="search"
                enterKeyHint="search"
                className="library-panel-search"
                placeholder="Rechercher dans la bibliothèque"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                aria-label="Rechercher dans la bibliothèque"
              />
            </label>
          ) : null}

          {view === 'preview' && selected ? (
            <div className="library-panel-preview lg-scroll">
              <LibraryThumb item={selected} alt={selected.name} />
              <p className="library-panel-name">{selected.name}</p>
              <p className="library-panel-meta">{formatEuroFr(selected.price)} × {selected.quantity || 1}</p>
              <button type="button" className="library-panel-text-btn" onClick={() => openEdit(selected)}>
                <img src="/Icon drive file rename outline.png" alt="" />
                Modifier
              </button>
            </div>
          ) : null}

          {view === 'edit' && draft ? (
            <form className="library-panel-edit lg-scroll" onSubmit={saveEdit}>
              <label>
                Nom
                <input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
              </label>
              <label>
                Prix
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={draft.price}
                  onChange={(event) => setDraft({ ...draft, price: event.target.value })}
                />
              </label>
              <label>
                Quantité
                <input
                  type="number"
                  inputMode="numeric"
                  min="1"
                  value={draft.quantity}
                  onChange={(event) => setDraft({ ...draft, quantity: event.target.value })}
                />
              </label>
              <label>
                Lien
                <input
                  type="url"
                  inputMode="url"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={draft.link}
                  onChange={(event) => setDraft({ ...draft, link: event.target.value })}
                />
              </label>
              {editError ? <p className="library-panel-error">{editError}</p> : null}
              <button type="submit" className="library-panel-text-btn" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </form>
          ) : null}

          {view === 'list' ? (
            <div className="library-panel-list lg-scroll">
              {(() => {
                const listState = describeLibraryListState({
                  isLoading,
                  data: isError ? undefined : items,
                  error: isError ? { response: { status: 500 }, message: errorMessage } : null,
                  query,
                })
                if (listState === 'loading') {
                  return <div className="library-panel-status">Chargement de la bibliothèque…</div>
                }
                if (listState === 'error' || listState === 'forbidden') {
                  return <div className="library-panel-status library-panel-error">{errorMessage || 'Impossible de charger la bibliothèque.'}</div>
                }
                if (listState === 'empty') {
                  return (
                    <div className="library-panel-status">
                      <img src="/Icon book.png" alt="" />
                      <p>Aucun élément dans la bibliothèque</p>
                    </div>
                  )
                }
                if (listState === 'search-empty') {
                  return (
                    <div className="library-panel-status">
                      <img src="/Icon search.png" alt="" />
                      <p>Aucun résultat pour cette recherche</p>
                    </div>
                  )
                }
                return visible.map((item) => (
                  <div
                    key={item.id}
                    className={`library-panel-row${importSuccessItem?.id === item.id ? ' is-success' : ''}`}
                  >
                    {importSuccessItem?.id === item.id ? (
                      <div className="library-success-overlay">
                        <Lottie animationData={null} loop={false} autoplay style={{ width: 56, height: 56 }} path="/Succes.json" />
                      </div>
                    ) : null}
                    {resolveCardImage(item).url || resolveCardImage(item).key ? (
                      <button
                        type="button"
                        className="library-panel-thumb"
                        onClick={() => openPreview(item)}
                        aria-label={`Agrandir ${item.name}`}
                      >
                        <LibraryThumb item={item} compact />
                        <img src="/Icon search.png" alt="" className="library-zoom-hint" />
                      </button>
                    ) : (
                      <div className="library-panel-thumb is-missing" aria-hidden="true">
                        <MissingImage compact />
                      </div>
                    )}
                    <div className="library-panel-copy">
                      <div className="library-panel-name">{item.name}</div>
                      <div className="library-panel-meta">{formatEuroFr(item.price)} × {item.quantity || 1}</div>
                    </div>
                    <button type="button" className="library-panel-icon-btn" onClick={() => openEdit(item)} aria-label={`Modifier ${item.name}`}>
                      <img src="/Icon drive file rename outline.png" alt="" />
                    </button>
                    <button
                      type="button"
                      className="library-plus-button"
                      onClick={() => onAddToCart?.(item.id)}
                      title="Ajouter au panier"
                      aria-label={`Ajouter ${item.name} au panier`}
                      disabled={adding}
                    >
                      <span className="library-plus-stack" aria-hidden="true">
                        <img src="/Icon shopping cart.png" alt="" />
                        <svg className="plus-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                          <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z" />
                        </svg>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="library-delete-button"
                      onClick={() => onDelete?.(item.id)}
                      title="Supprimer"
                      aria-label={`Supprimer ${item.name}`}
                      disabled={deleting}
                    >
                      <Lottie animationData={null} loop autoplay style={{ width: 28, height: 28 }} path="/Red_trash.json" />
                    </button>
                  </div>
                ))
              })()}
            </div>
          ) : null}
        </div>
      </aside>
    </div>
  )
}
